import memoize from 'memoizee';
import config from '@/constants/config';
import pools from '@/constants/pools';
import getPoolVirtualPrice from '@/utils/data/getPoolVirtualPrice';

const mainUiPoolsAddresses = pools.map(({ addresses }) => addresses.swap.toLowerCase());

// import { IS_DEV } from '@/constants/AppConstants';

const getFactoryV2Pools = memoize(async () => {
  const hasFactoryRegistry = !!config.factoryV2RegistryAddress;
  if (!hasFactoryRegistry) return [];

  let gaugesData;

  const [
    { data: { poolDetails: apyData } },
    { data: { poolData } },
    { data },
  ] = await Promise.all([
    (await fetch(config.factoryV2ApyEndpoint)).json(),
    (await fetch(config.factoryV2PoolsEndpoint)).json(),
    config.factoryV2GaugesEndpoint ? (await fetch(config.factoryV2GaugesEndpoint)).json() : { data: { gauges: {} } },
  ]);

  if (data.gauges) gaugesData = data.gauges;
  else gaugesData = data;

  // Exclude lending vault gauges
  gaugesData = Object.values(gaugesData).filter(({ isPool }) => isPool !== false); // isPool can be either bool or undefined depending on the source

  const factoryGaugesData = gaugesData.filter(({ factory }) => (
    config.blockchainId !== 'ethereum' || // Sidechain gauge endpoints are for factories only, include all gauges
    factory === true // On ethereum, include only factory gauges
  ));

  const usdRates = await Promise.all(poolData.map(async (pool) => {
    let virtualPrice;
    const poolAddress = pool.addresses?.swap || pool.address;
    if (poolAddress && pool.totalSupply > 0) {
      virtualPrice = await getPoolVirtualPrice(poolAddress);
    }

    return (pool.usdTotal / ((parseFloat(pool.totalSupply) === 0 ? 1 : pool.totalSupply) / 1e18 * (virtualPrice || 1)));
  }));

  const augmentedData = poolData.map((poolInfo, i) => {
    const factoryGaugeData = factoryGaugesData.find(({ swap }) => swap.toLowerCase() === poolInfo.address.toLowerCase());

    return {
      ...poolInfo,
      name: `Factory:${poolInfo.name.split(/pool:/i)[1]}`,
      referenceAsset: poolInfo.assetTypeName,
      apy: apyData[i]?.apy || 0,
      apyWeekly: apyData[i]?.apyWeekly || 0,
      volume: apyData[i]?.volume ? (apyData[i]?.volume * usdRates[i]) : 0,
      coinsAddresses: poolInfo.coinsAddresses.filter((address) => address !== '0x0000000000000000000000000000000000000000'),
      gauge: factoryGaugeData?.gauge,
      rewardsNeedNudging: factoryGaugeData?.rewardsNeedNudging,
      areCrvRewardsStuckInBridge: factoryGaugeData?.areCrvRewardsStuckInBridge,
      gaugeRewards: factoryGaugeData?.extraRewards || poolInfo.gaugeRewards,
    };
  });

  const filteredData = augmentedData.filter(({ address }) => {
    const lcAddress = address.toLowerCase();
    const isMainUiPool = config.blockchainId !== 'kava' && config.blockchainId !== 'celo' && mainUiPoolsAddresses.some((address) => address === lcAddress);

    return !isMainUiPool;
  });

  return filteredData;
}, {
  promise: true,
  maxAge: 60 * 1000, // 1m
});

export default getFactoryV2Pools;
